import { useState } from 'react';
import { XIcon as X } from './ui/Icons';
import { useAppStore, recalculateStore } from '@/store/useAppStore';
import { createCustomScale, validateCustomScale } from '@gpa-scales/shared-logic/engine/homeschoolEngine';
import { ScrubInput } from 'calc-controls';

interface ScaleEntry {
  grade: string;
  points: number;
}

export function HomeschoolMode() {
  const settings = useAppStore((s) => s.settings);
  const updateSettings = useAppStore((s) => s.updateSettings);

  const [scaleEntries, setScaleEntries] = useState<ScaleEntry[]>(
    settings.gradeScaleMap.map((m) => ({ grade: m.grade, points: m.points }))
  );
  const [errors, setErrors] = useState<string[]>([]);

  const toggleHomeschool = () => {
    updateSettings({ homeschoolMode: !settings.homeschoolMode });
    recalculateStore();
  };

  const addEntry = () => {
    setScaleEntries((prev) => [...prev, { grade: '', points: 0 }]);
  };

  const removeEntry = (index: number) => {
    setScaleEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: 'grade' | 'points', value: string | number) => {
    setScaleEntries((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const saveScale = () => {
    const validationErrors = validateCustomScale(scaleEntries);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    const customScale = createCustomScale(scaleEntries);
    updateSettings({ gradeScaleMap: customScale });
    recalculateStore();
  };

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text)]">Homeschool Mode</h3>
        <label className="flex items-center gap-2" htmlFor="homeschool-toggle">
          <input
            type="checkbox"
            id="homeschool-toggle"
            checked={settings.homeschoolMode ?? false}
            onChange={toggleHomeschool}
            className="h-4 w-4 rounded border-border"
          />
          <span className="text-xs text-[var(--color-text-secondary)]">Enable</span>
        </label>
      </div>

      {settings.homeschoolMode && (
        <div className="space-y-3">
          <p className="text-xs text-[var(--color-text-muted)]">
            Custom grading scale. Define your own grade-to-points mapping.
          </p>

          <div className="space-y-1">
            {scaleEntries.map((entry, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={entry.grade}
                  onChange={(e) => updateEntry(i, 'grade', e.target.value)}
                  className="w-20 rounded border border-border px-2 py-1 text-xs"
                  placeholder="Grade"
                  aria-label={`Grade for entry ${i + 1}`}
                />
                <span className="text-xs text-[var(--color-text-muted)]">=</span>
                <ScrubInput
                  value={entry.points}
                  onChange={(v) => updateEntry(i, 'points', v)}
                  label={`Points for entry ${i + 1}`}
                  min={0}
                  step={0.1}
                  decimals={1}
                  inline
                />
                <span className="text-xs text-[var(--color-text-muted)]">pts</span>
                <button
                  onClick={() => removeEntry(i)}
                  className="text-xs text-error hover:text-error-hover"
                  aria-label={`Remove grade entry ${i + 1}`}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={addEntry}
              className="rounded-md bg-[var(--color-bg-tertiary)] px-3 py-1 text-xs font-medium text-[var(--color-text)]"
            >
              + Add Grade
            </button>
            <button
              onClick={saveScale}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-white"
            >
              Save Scale
            </button>
          </div>

          {errors.length > 0 && (
            <div className="rounded border border-error bg-error-light p-2">
              {errors.map((err, i) => (
                <p key={i} className="text-xs text-error">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
